import React, { useCallback, useMemo, useRef, useState } from 'react';

import { iaRa } from 'lib/helpers/utility';

import { useReactFilters } from 'components/shared/Filters/hooks/useReactFilters';

import { TableContext } from './TableContext';
import { formatValue } from './helper';
import { useGridApi } from './hooks/useGridApi';
import { useTablePreferences } from './hooks/useTablePreferences';
import { useTableSearchParams } from './hooks/useTableSearchParams';

export const TableContextProvider = ({
  cols,
  name,
  pagination,
  children,
  defaultFilters,
  onSelect = () => {},
  getData,
  exportParams,
  exportColumnsKeys = ['all'],
  queryParams,
  title,
  paramPrefix = name,
  switchItems = [],
  isInfinite,
  selectedRows: selectedRowsProp
}) => {
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState({});
  const [forceRefresh, setForceRefresh] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const gridRef = useRef();

  const { gridApi, onGridReady, selectedRows, setSelectedRows, onSelectionChanged } = useGridApi({
    onSelect,
    selectedRowsProp
  });

  const { colDefs, setColDefs, limit, setLimit, persistColumns } = useTablePreferences({
    name,
    cols
  });

  const { onPageChange, resetSortAndPage, onSortChange, view } = useTableSearchParams(
    paramPrefix,
    isInfinite
  );

  const isSwitchItemView = useMemo(() => {
    return iaRa(switchItems).some((item) => item.value === view);
  }, [switchItems, view]);

  const {
    filters,
    initialFilters,
    filtersInitialized,
    isEditingFilter,
    updateFilters,
    resetFilters,
    changeFilters
  } = useReactFilters({
    name,
    defaultFilters
  });

  const onSortChanged = useCallback(() => {
    if (!gridApi) return;
    const currentSort = gridApi
      .getColumnState()
      .filter((col) => col.sort === 'asc' || col.sort === 'desc');
    setColDefs(gridApi.getColumnDefs());
    onSortChange(currentSort);
  }, [gridApi, onSortChange]);

  const onColumnMoved = useCallback(async () => {
    if (!gridApi) return;
    const currentColumns = gridApi.getColumnDefs();
    await persistColumns(currentColumns);
  }, [gridApi, persistColumns]);

  const onFilterChange = useCallback(
    async (callback, { resetParams = false } = {}) => {
      if (resetParams) await resetSortAndPage();
      changeFilters(callback, isSwitchItemView);
    },
    [resetSortAndPage, changeFilters, isSwitchItemView]
  );

  return (
    <TableContext.Provider
      value={{
        limit,
        setLimit,
        colDefs,
        gridApi,
        onGridReady,
        onColumnMoved,
        setColDefs,
        onSortChanged,
        onPageChange,
        resetSortAndPage,
        setPage,
        name,
        page,
        formatValue,
        tableLoading,
        setTableLoading,
        onSelectionChanged,
        selectedRows,
        setSelectedRows,
        pagination,
        filters,
        initialFilters,
        isEditingFilter,
        gridRef,
        defaultFilters,
        errors,
        setErrors,
        forceRefresh,
        setForceRefresh,
        getData,
        exportParams,
        exportColumnsKeys,
        queryParams,
        title,
        selectedItem,
        setSelectedItem,
        showModal,
        setShowModal,
        filtersInitialized,
        updateFilters,
        resetFilters,
        setFilters: changeFilters,
        changeFilters: onFilterChange
      }}>
      {children}
    </TableContext.Provider>
  );
};
